// highlight.js se carga de forma perezosa (import dinámico) la primera vez que
// se invoca highlightCodeBlocks, para mantenerlo fuera del bundle inicial.
// La promesa se cachea a nivel de módulo: solo se descarga y registra una vez.
let hljsPromise = null;

function loadHljs() {
  if (!hljsPromise) {
    hljsPromise = (async () => {
      const [
        { default: hljs },
        { default: javascript },
        { default: xml },
        { default: css },
        { default: python },
        { default: php },
        { default: sql },
        { default: bash },
        { default: json },
        { default: typescript },
        { default: yaml },
        { default: markdown },
      ] = await Promise.all([
        import('highlight.js/lib/core'),
        import('highlight.js/lib/languages/javascript'),
        import('highlight.js/lib/languages/xml'),
        import('highlight.js/lib/languages/css'),
        import('highlight.js/lib/languages/python'),
        import('highlight.js/lib/languages/php'),
        import('highlight.js/lib/languages/sql'),
        import('highlight.js/lib/languages/bash'),
        import('highlight.js/lib/languages/json'),
        import('highlight.js/lib/languages/typescript'),
        import('highlight.js/lib/languages/yaml'),
        import('highlight.js/lib/languages/markdown'),
      ]);

      hljs.registerLanguage('javascript', javascript);
      hljs.registerLanguage('js', javascript);
      hljs.registerLanguage('xml', xml);
      hljs.registerLanguage('html', xml);
      hljs.registerLanguage('css', css);
      hljs.registerLanguage('python', python);
      hljs.registerLanguage('php', php);
      hljs.registerLanguage('sql', sql);
      hljs.registerLanguage('bash', bash);
      hljs.registerLanguage('shell', bash);
      hljs.registerLanguage('json', json);
      hljs.registerLanguage('typescript', typescript);
      hljs.registerLanguage('ts', typescript);
      hljs.registerLanguage('yaml', yaml);
      hljs.registerLanguage('markdown', markdown);

      return hljs;
    })();
  }
  return hljsPromise;
}

/**
 * Aplica syntax highlighting a todos los <pre> dentro de un contenedor DOM.
 * Extrae el texto plano del <pre>, detecta el lenguaje y reemplaza el innerHTML.
 * Async: carga highlight.js bajo demanda la primera vez.
 */
export async function highlightCodeBlocks(container) {
  if (!container) return;

  const hljs = await loadHljs();

  const preBlocks = container.querySelectorAll('pre');
  preBlocks.forEach((pre) => {
    // No re-procesar bloques ya resaltados
    if (pre.dataset.highlighted === 'true') return;

    // Extraer texto plano (ignorar <p>, <br>, <div> del contentEditable)
    const text = getPlainTextFromPre(pre);
    if (!text.trim()) return;

    // Detectar lenguaje: JSON puro / JSON-LD tiene prioridad
    let language = detectJsonLd(text);
    let highlighted = null;

    if (language === 'json') {
      const hasScriptTag = /<script[^>]*>/i.test(text);
      try {
        if (hasScriptTag) {
          // JSON-LD: usar auto restringido a xml+json para colorear script + contenido
          const auto = hljs.highlightAuto(text, ['xml', 'json']);
          highlighted = auto.value;
        } else {
          highlighted = hljs.highlight(text, { language: 'json' }).value;
        }
      } catch (e) {
        highlighted = null;
      }
    }

    if (!highlighted) {
      const result = hljs.highlightAuto(text);
      if (result.language && result.relevance >= 5) {
        language = result.language;
        highlighted = result.value;
      }
    }

    // Crear <code> con el resultado (o texto plano escapado si no hay highlight)
    const code = document.createElement('code');
    if (highlighted) {
      code.className = `hljs language-${language}`;
      code.innerHTML = highlighted;
    } else {
      code.className = 'hljs';
      code.textContent = text;
      language = 'code';
    }

    pre.innerHTML = '';
    pre.appendChild(code);
    pre.dataset.highlighted = 'true';
    pre.classList.add('hljs-pre');

    // Cabecera: badge del lenguaje (siempre presente)
    const badge = document.createElement('span');
    badge.className = 'code-lang-badge';
    badge.textContent = language;
    pre.appendChild(badge);

    // Botón copiar
    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'code-copy-btn';
    copyBtn.setAttribute('aria-label', 'Copiar código');
    copyBtn.title = 'Copiar';
    copyBtn.innerHTML = copyIconSvg();
    copyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const textToCopy = code.innerText;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy).then(() => showCopied(copyBtn));
      } else {
        fallbackCopy(textToCopy);
        showCopied(copyBtn);
      }
    });
    pre.appendChild(copyBtn);
  });
}

function detectJsonLd(text) {
  const trimmed = text.trim();
  // Fragmento con <script type="application/ld+json">...</script>
  if (/<script[^>]*application\/(ld\+)?json[^>]*>/i.test(trimmed)) {
    return 'json';
  }
  // JSON puro empieza por { o [
  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  if ((first === '{' && last === '}') || (first === '[' && last === ']')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch (e) {
      return null;
    }
  }
  return null;
}

function copyIconSvg() {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
}

function checkIconSvg() {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
}

function showCopied(btn) {
  btn.classList.add('copied');
  btn.innerHTML = checkIconSvg();
  setTimeout(() => {
    btn.classList.remove('copied');
    btn.innerHTML = copyIconSvg();
  }, 1600);
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch (e) {}
  document.body.removeChild(ta);
}

/**
 * Extrae texto plano de un <pre> que puede contener <p>, <br>, <div>
 * (producidos por contentEditable)
 */
function getPlainTextFromPre(pre) {
  // Clonar para no modificar el original durante la lectura
  const clone = pre.cloneNode(true);

  // Reemplazar <br> por saltos de línea
  clone.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n');
  });

  // Reemplazar <p> y <div> por su texto + salto de línea
  clone.querySelectorAll('p, div').forEach((el) => {
    el.replaceWith(el.textContent + '\n');
  });

  return clone.textContent || '';
}
